import {ChangeDetectorRef, Component, OnInit,
  CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import {CacheUpdateService} from '@app/services/cache-update.service';
import {DcpTransactionService} from '@app/services/dcp-transaction.service';
import {ConfigService, TransactionList, LoadingPageComponent} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AuthStorageService, GlobalMessageService, GlobalMessageType, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {Observable, of} from 'rxjs';
import {switchMap, take, tap} from 'rxjs/operators';
import {convertUrlToCurrentHostname} from './../shared/url-utils';
import {getAuthorized} from './../shared/getAuthorized';
import {formatTestPageError} from './../shared/format-error';
import {TabNavigationComponent} from '../tab-navigation/tab-navigation.component';
import {IframeComponent} from '../../shared/iframe/iframe.component';
import {CdkCopyToClipboard} from '@angular/cdk/clipboard';
import {AsyncPipe} from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'dcp-contract-list-page',
  templateUrl: './transaction-list-page.component.html',
  styleUrls: ['./transaction-list-page.component.scss'],
  imports: [LoadingPageComponent, TabNavigationComponent, IframeComponent, CdkCopyToClipboard, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TransactionListPageComponent implements OnInit {
  public sUserId: string = '';
  public currentUser: string;
  public isAuthorized: boolean;
  public user$: Observable<User>;
  public transactions$: Observable<TransactionList>;
  public previewUrl: string;
  public loadingState: boolean = false;
  public loadingScreenText: string = 'Please wait while the cached list is updated';
  public currentUserScimUserId: string;

  constructor(
    private dcpTransactionService: DcpTransactionService,
    private userFacade: UserAccountFacade,
    private globalMessageService: GlobalMessageService,
    private authStorageService: AuthStorageService,
    private configService: ConfigService,
    private cacheUpdateService: CacheUpdateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.user$ = this.userFacade.get();

    this.user$.pipe(untilDestroyed(this), take(1)).subscribe((user) => {
      this.currentUserScimUserId = user.scimUserId;
    });

    this.getMyTransactions();
    this.getAuthorizedRoles();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.sUserId = target.value;
  }

  onReset() {
    this.sUserId = '';
    this.transactions$ = of(undefined);
  }

  getMyTransactions() {
    if (!/^S|^P/.test(this.currentUserScimUserId)) return;

    this.getTransactions();
  }

  getTransactions(userId?: string): void {
    this.dcpTransactionService
      .getTransactions(userId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.currentUser = userId || this.currentUserScimUserId;
          this.transactions$ = of(response);
        },
        error: (error) => {
          this.currentUser = undefined;
          this.transactions$ = of(undefined);
          this.globalMessageService.add(
            formatTestPageError(error),
            GlobalMessageType.MSG_TYPE_ERROR,
            60000,
          );
        },
      });
  }

  getAuthorizedRoles(): void {
    getAuthorized(this.authStorageService, this.configService, this);
  }

  hasPreviewUrl(): boolean {
    return this.previewUrl?.length > 0;
  }

  togglePreview(targetUrl: string): void {
    this.previewUrl = this.previewUrl ? null : targetUrl;
    this.cdr.detectChanges();
  }

  convertUrlToCurrentHostname = convertUrlToCurrentHostname;

  updateCache(userId?: string): void {
    this.loadingState = true;
    this.cacheUpdateService
      .updateTransactionsCache('user', userId)
      .pipe(
        untilDestroyed(this),
        switchMap(() =>
          this.cacheUpdateService
            .updateTransactionsCache('customer', userId)
            .pipe(
              untilDestroyed(this),
              tap({
                next: () => { this.currentUser = userId; },
                error: (error) => {
                  this.currentUser = undefined;
                  this.globalMessageService.add(
                    formatTestPageError(error),
                    GlobalMessageType.MSG_TYPE_ERROR,
                    60000,
                  );
                },
              }),
            )
        ),
      )
      .subscribe({
        next: () => {
          this.currentUser = userId;
          this.loadingState = false;
        },
        error: (error) => {
          this.currentUser = undefined;
          this.loadingState = false;
          this.globalMessageService.add(
            formatTestPageError(error),
            GlobalMessageType.MSG_TYPE_ERROR,
            60000,
          );
        },
      });
  }
}
